import React from 'react';
import { Heart, ShoppingCart, Eye, Download, Check } from 'lucide-react';
import clsx from 'clsx';
import { useCart } from '@context/CartContext';
import { useAuth } from '@context/AuthContext';
import { useFavorites } from '@context/FavoritesContext';
import { formatCurrency } from '@utils/helpers';
import { PRICING } from '@utils/constants';
import Button from '@components/common/Button';
import ResponsiveImage from '@components/common/ResponsiveImage';
import type { PhotoCardProps } from '@/types';

const PhotoCard: React.FC<PhotoCardProps> = ({
  photo,
  onView,
  showActions = true,
  selectable = true,
  className,
}) => {
  const { addToCart, isInCart } = useCart();
  const { user } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();

  const isFavorited = isFavorite(photo.id);

  const inCart = isInCart(photo.id);
  const webPrice = PRICING.resolutions.web.price;

  const handleAddToCart = () => {
    addToCart({
      photoId: photo.id,
      eventId: photo.eventId,
      title: photo.title || `Photo ${photo.id}`,
      thumbnail: photo.thumbnail,
      resolution: 'web',
      price: webPrice,
      quantity: 1,
    });
  };

  const handleToggleFavorite = () => {
    toggleFavorite(photo);
  };

  const handleView = () => {
    onView(photo);
  };

  return (
    <div
      className={clsx(
        'group relative bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200',
        className
      )}
      onClick={handleView}
    >
      {/* Image Container */}
      <div className="relative aspect-square bg-gray-100">
        <ResponsiveImage
          src={photo.thumbnail}
          alt={photo.title || `Photo ${photo.id}`}
          sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
          className="w-full h-full"
        />

        {/* Overlay actions */}
        {showActions && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex gap-2">
              <Button
                size="small"
                variant="ghost"
                className="bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700"
                onClick={handleView}
                icon={<Eye size={16} />}
              />

              {user && (
                <Button
                  size="small"
                  variant="ghost"
                  className="bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700"
                  onClick={handleToggleFavorite}
                  icon={
                    <Heart
                      size={16}
                      className={clsx(
                        'transition-colors',
                        isFavorited ? 'fill-red-500 text-red-500' : ''
                      )}
                    />
                  }
                />
              )}
            </div>
          </div>
        )}

        {/* Selection indicator */}
        {inCart && (
          <div className="absolute top-2 right-2 w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center animate-zoom-in">
            <Check size={12} className="text-white" />
          </div>
        )}

        {/* Photo metadata overlay */}
        {photo.metadata && (
          <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {photo.metadata.width} × {photo.metadata.height}
          </div>
        )}
      </div>

      {/* Photo Info */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {photo.title || `Photo ${photo.id}`}
            </h3>
            {photo.description && (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{photo.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900">
              From {formatCurrency(webPrice)}
            </span>
            <span className="text-xs text-gray-500">
              {new Date(photo.createdAt).toLocaleDateString()}
            </span>
          </div>

          {selectable && (
            <Button
              size="small"
              variant={inCart ? 'secondary' : 'primary'}
              onClick={handleAddToCart}
              disabled={inCart}
              className="flex-shrink-0 min-w-[80px]"
              icon={inCart ? <Download size={14} /> : <ShoppingCart size={14} />}
            >
              {inCart ? 'Added' : 'Select'}
            </Button>
          )}
        </div>

        {/* Tags */}
        {photo.tags && photo.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {photo.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
            {photo.tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                +{photo.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoCard;
